import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { MembershipPackageListRow } from '../../../../core/models/finance/master-fee.model';
import { ApiMasterFeeService } from '../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalMembershipPackageComponent } from '../../modals/parking/modal-membership-package/modal-membership-package.component';

@Component({
  selector: 'app-membership-packages',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalMembershipPackageComponent],
  templateUrl: './membership-packages.component.html',
  styleUrl: './membership-packages.component.css',
})
export class MembershipPackagesComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<MembershipPackageListRow[]>([]);
  filteredItems = signal<MembershipPackageListRow[]>([]);
  isLoading = signal(false);
  searchText = signal('');

  isModalOpen = signal(false);
  editSubscriptionId = signal<number | null>(null);

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) {
        untracked(() => {
          this.items.set([]);
          this.filteredItems.set([]);
        });
        return;
      }
      untracked(() => this.loadPackages());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) {
      this.loadPackages();
    }
  }

  onRefresh(): void {
    this.loadPackages();
  }

  onSearch(): void {
    const q = this.searchText().trim().toLowerCase();
    if (!q) {
      this.filteredItems.set(this.items());
      return;
    }
    this.filteredItems.set(
      this.items().filter(
        (x) =>
          x.name.toLowerCase().includes(q) ||
          x.vehicleName.toLowerCase().includes(q) ||
          String(x.subscriptionId).includes(q)
      )
    );
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    this.editSubscriptionId.set(null);
    this.isModalOpen.set(true);
  }

  openEdit(item: MembershipPackageListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!item.subscriptionId) {
      this.toast.error('ไม่พบ Subscription ID');
      return;
    }
    this.editSubscriptionId.set(item.subscriptionId);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.editSubscriptionId.set(null);
  }

  onSaved(): void {
    this.closeModal();
    this.loadPackages();
  }

  private loadPackages(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      this.filteredItems.set([]);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.api.getMembershipPackages(siteId).subscribe({
      next: (rows) => {
        this.items.set(rows);
        this.onSearch();
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.filteredItems.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดแพ็กเกจสมาชิกล้มเหลว');
      },
    });
  }
}
